/*
Copyright 2025 Element Creations Ltd.

SPDX-License-IdFentifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type LocalParticipant,
  type ScreenShareCaptureOptions,
  type TrackPublishOptions,
  RoomEvent,
  MediaDeviceFailure,
} from "livekit-client";
import {
  Status as RTCSessionStatus,
  type LivekitTransport,
  type LivekitTransportConfig,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import { type Logger } from "matrix-js-sdk/lib/logger";
import { deepCompare } from "matrix-js-sdk/lib/utils";
import { type CallMembershipIdentityParts } from "matrix-js-sdk/lib/matrixrtc/EncryptionManager";

import { type Behavior, computed, fromEvents, state } from "../../Behavior.ts";
import { type IConnectionManager } from "../remoteMembers/ConnectionManager.ts";
import { type Scope } from "../../Scope.ts";
import { type Publisher } from "./Publisher.ts";
import { type MuteStates } from "../../MuteStates.ts";
import {
  ElementCallError,
  FailToStartLivekitConnection,
  MembershipManagerError,
  UnknownCallError,
} from "../../../utils/errors.ts";
import { ElementWidgetActions, widget } from "../../../widget.ts";
import { getUrlParams } from "../../../UrlParams.ts";
import { PosthogAnalytics } from "../../../analytics/PosthogAnalytics.ts";
import {
  advancedScreenShare,
  screenShareResolution,
  screenShareFramerate,
  screenShareBitrate,
  screenShareCodec,
  parseResolution,
} from "../../../settings/settings.ts";
import { MatrixRTCMode } from "../../../config/ConfigOptions.ts";
import { Config } from "../../../config/Config.ts";
import {
  ConnectionState,
  type Connection,
} from "../remoteMembers/Connection.ts";
import { type HomeserverConnected } from "./HomeserverConnected.ts";
import { type LocalTransport } from "./LocalTransport.ts";
import { areLivekitTransportsEqual } from "../remoteMembers/MatrixLivekitMembers.ts";
import { PUBLICATION_EVENTS } from "../../trackReference.ts";
import { consentOnJoin } from "../../Recording.ts";
import { computeSlotId } from "matrix-js-sdk/lib/matrixrtc/utils";

export enum TransportState {
  /** Not even a transport is available to the LocalMembership */
  Waiting = "transport_waiting",
}

export enum PublishState {
  WaitingForUser = "publish_waiting_for_user",
  // XXX: This state is removed for now since we do not have full control over
  // track publication anymore with the publisher abstraction, might come back in the future?
  // /** Implies lk connection is connected */
  // Starting = "publish_start_publishing",
  /** Implies lk connection is connected */
  Publishing = "publish_publishing",
}

// TODO not sure how to map that correctly with the
// new publisher that does not manage tracks itself anymore
export enum TrackState {
  /** The track is waiting for user input to create tracks (waiting to call `startTracks()`) */
  WaitingForUser = "tracks_waiting_for_user",
  // XXX: This state is removed for now since we do not have full control over
  // track creation anymore with the publisher abstraction, might come back in the future?
  // /** Implies lk connection is connected */
  // Creating = "tracks_creating",
  /** Implies lk connection is connected */
  Ready = "tracks_ready",
}

export type LocalMemberMediaState =
  | {
      tracks: TrackState;
      /** The state of the local connection, or `null` if there is none yet. */
      connection: ConnectionState | Error | null;
    }
  | PublishState
  | ElementCallError;
export type LocalMemberState =
  | ElementCallError
  | TransportState.Waiting
  | {
      media: LocalMemberMediaState;
      matrix: ElementCallError | RTCSessionStatus;
    };

/*
 * - get transport to use
 * - get openId + jwt token
 * - wait for createTrack() call
 *    - create tracks
 * - wait for join() call
 *   - Publisher.publishTracks()
 *   - send join state/sticky event
 */

interface Props {
  // TODO add a comment into some code style readme or file header callviewmodel
  // that the inputs for those createSomething$() functions should NOT contain any js-sdk objectes
  scope: Scope;
  muteStates: MuteStates;
  connectionManager: IConnectionManager;
  createPublisherFactory: (connection: Connection) => Publisher;
  joinMatrixRTC: (transport: LivekitTransportConfig) => void;
  homeserverConnected: HomeserverConnected;
  roomId: string;
  localTransport$: Behavior<LocalTransport>;
  matrixRTCSession: Pick<
    MatrixRTCSession,
    "updateCallIntent" | "leaveRoomSession"
  >;
  logger: Logger;
}

/**
 * This class is responsible for managing the own membership in a room.
 * We want
 *  - a publisher
 *  -
 * @param props The properties required to create the local membership.
 * @param props.scope The observable scope to use.
 * @param props.connectionManager The connection manager to get connections from.
 * @param props.createPublisherFactory Factory to create a publisher once we have a connection.
 * @param props.joinMatrixRTC Callback to join the matrix RTC session once we have a transport.
 * @param props.homeserverConnected The homeserver connected state.
 * @param props.localTransport$ The transport to advertise in our membership.
 * @param props.logger The logger to use.
 * @param props.muteStates The mute states for video and audio.
 * @param props.matrixRTCSession The matrix RTC session to join.
 * @param props.roomId The room ID used as the call identifier in analytics events.
 * @returns
 *  - publisher: The handle to create tracks and publish them to the room.
 *  - connected$: the current connection state. Including matrix server and livekit server connection. (only considering the livekit server we are using for our own media publication)
 *  - transport$: the transport object the ownMembership$ ended up using.
 *  - connectionState: the current connection state. Including matrix server and livekit server connection.
 *  - sharingScreen$: Whether we are sharing our screen. `undefined` if we cannot share the screen.
 */
export const createLocalMembership$ = ({
  scope,
  connectionManager,
  localTransport$,
  homeserverConnected,
  createPublisherFactory,
  joinMatrixRTC,
  logger: parentLogger,
  muteStates,
  matrixRTCSession,
  roomId,
}: Props): {
  /**
   * This request to start audio and video tracks.
   * Can be called early to pre-emptively get media permissions and start devices.
   */
  startTracks: () => void;
  /**
   * This sets a inner state (shouldPublish) to true and instructs the js-sdk and livekit to keep the user
   * connected to matrix and livekit.
   */
  requestJoinAndPublish: () => void;
  requestDisconnect: () => void;
  localMemberState$: Behavior<LocalMemberState>;
  sharingScreen$: Behavior<boolean>;
  /**
   * Callback to toggle screen sharing. If null, screen sharing is not possible.
   */
  toggleScreenSharing: (() => void) | null;
  // tracks$: Behavior<LocalTrack[]>;
  participant$: Behavior<LocalParticipant | null>;
  connection$: Behavior<Connection | null>;
  /**
   * Tracks the homserver and livekit connected state and based on that computes reconnecting.
   */
  reconnecting$: Behavior<boolean>;
  /** Shorthand for homeserverConnected.rtcSession === Status.Disconnected
   * Direct translation to the js-sdk membership manager connection `Status`.
   */
  disconnected$: Behavior<boolean>;
  /**
   * Fully connected
   */
  connected$: Behavior<boolean>;
  internalLoggerRef: Logger;
} => {
  const logger = parentLogger.getChild("[LocalMembership]");
  logger.debug(`Creating local membership..`);

  const transportsEqual = (
    a: LivekitTransportConfig | null,
    b: LivekitTransportConfig | null,
  ): boolean =>
    a === b || (a !== null && b !== null && areLivekitTransportsEqual(a, b));

  // We consider errors on the transport as fatal, whether they concern the
  // active transport or the preferred transport.
  const fatalTransportError$ = computed<ElementCallError | null>(() => {
    const e = localTransport$.value.error$.value;
    if (e === null) return null;
    return e instanceof ElementCallError
      ? e
      : new UnknownCallError(
          e instanceof Error
            ? e
            : new Error("Unknown error from localTransport"),
        );
  });

  // This is the transport that we will advertise in our membership.
  const advertisedTransport$ = computed(
    () => localTransport$.value.advertised$.value,
    transportsEqual,
  );

  const activeTransport$ = computed(() => {
    const transport = localTransport$.value;
    // Our policy is to not publish to another transport if our preferred
    // transport is misconfigured
    if (transport.advertised$.value == null) return null;
    return transport.active$.value?.transport ?? null;
  }, transportsEqual);

  const localConnection$ = computed(() => {
    const transport = activeTransport$.value;
    if (transport === null) return null;
    return connectionManager.connectionManagerData$.value.getConnectionForTransport(
      transport,
    );
  });
  scope.effect(() => {
    logger.info(
      `Local connection updated: ${localConnection$.value?.transport?.livekit_service_url}`,
    );
  });

  // Media device errors can be PermissionDenied, NotFound, DeviceInUse, Other.
  // They also occur if you cancel the screen sharing browser prompt.
  // This is not necessarily fatal, since the user might be able to join without media.
  // XXX We might want to give some user feedback here to let them know their media is not working.
  scope.effect(() => {
    const connection = localConnection$.value;
    if (!connection) return;
    const onMediaDevicesError = (error: Error): void => {
      const failure = MediaDeviceFailure.getFailure(error);
      if (failure) logger.error(`Failed to create local tracks:`, failure);
    };
    connection.livekitRoom.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
    return (): void => {
      connection.livekitRoom.off(
        RoomEvent.MediaDevicesError,
        onMediaDevicesError,
      );
    };
  });

  // MATRIX RELATED

  // Whether the user has asked for tracks to be started. This can happen before
  // the transport has resolved.
  const tracksRequested$ = state(false);
  // Whether the user has asked to join the call and publish media.
  const joinAndPublishRequested$ = state(false);

  /**
   * The publisher is stored in here an abstracts creating and publishing tracks.
   */
  const publisher$ = state<Publisher | null>(null);

  const startTracks = (): void => {
    tracksRequested$.value = true;
    // This used to return the tracks, but now they are only accessible via the publisher.
  };

  const requestJoinAndPublish = (): void => {
    tracksRequested$.value = true;
    joinAndPublishRequested$.value = true;
  };

  const requestDisconnect = (): void => {
    joinAndPublishRequested$.value = false;
  };

  // Take care of the publisher$
  // create a new one as soon as a local Connection is available
  //
  // Recreate a new one once the local connection changes
  //  - stop publishing
  //  - destruct all current streams
  //  - overwrite current publisher
  scope.reconcile(localConnection$, async (connection) => {
    logger.info(
      "reconcile based on new localConnection:",
      connection?.transport.livekit_service_url,
    );
    if (connection !== null) {
      const publisher = createPublisherFactory(connection);
      publisher$.value = publisher;

      // Clean-up callback
      return Promise.resolve(async (): Promise<void> => {
        await publisher.destroy();
      });
    }
  });

  // Use reconcile here to not run concurrent createAndSetupTracks calls
  scope.reconcile(
    computed(() => (tracksRequested$.value ? publisher$.value : null)),
    async (publisher) => {
      if (publisher) {
        await publisher.createAndSetupTracks().catch((e) => logger.error(e));
      }
    },
  );

  // Based on `joinAndPublishRequested$` we start publishing tracks. (once they are there!)
  scope.reconcile(
    computed(() => [publisher$.value, joinAndPublishRequested$.value] as const),
    async ([publisher, shouldJoinAndPublish]) => {
      // Get the current publishing state to avoid redundant calls.
      const isPublishing = publisher?.shouldPublish === true;
      if (shouldJoinAndPublish && !isPublishing) {
        try {
          await publisher?.startPublishing();
        } catch (error) {
          const message =
            error instanceof Error ? error.message : String(error);
          setPublishError(new FailToStartLivekitConnection(message));
        }
      } else if (isPublishing) {
        try {
          await publisher?.stopPublishing();
        } catch (error) {
          setPublishError(new UnknownCallError(error as Error));
        }
      }
    },
  );

  // STATE COMPUTATION

  // These are non fatal since we can join a room and consume media even though publishing failed.
  const publishError$ = state<ElementCallError | null>(null);
  const setPublishError = (e: ElementCallError): void => {
    if (publishError$.value !== null) {
      logger.error("Multiple Media Errors:", e);
    } else {
      publishError$.value = e;
    }
  };

  const localConnectionState$ = computed(
    () => localConnection$.value?.state$.value ?? null,
  );

  const mediaState$: Behavior<LocalMemberMediaState | null> = computed(() => {
    if (!activeTransport$.value) return null;
    const trackState: TrackState = tracksRequested$.value
      ? TrackState.Ready
      : TrackState.WaitingForUser;
    const connection = localConnectionState$.value;
    if (
      connection !== ConnectionState.LivekitConnected ||
      trackState !== TrackState.Ready
    )
      return { connection, tracks: trackState };
    if (!joinAndPublishRequested$.value) return PublishState.WaitingForUser;
    return PublishState.Publishing;
  }, deepCompare);

  const fatalMatrixError$ = state<ElementCallError | null>(null);
  const setMatrixError = (e: ElementCallError): void => {
    if (fatalMatrixError$.value !== null) {
      logger.error("Multiple Matrix Errors:", e);
    } else {
      fatalMatrixError$.value = e;
    }
  };

  const localMemberState$ = computed<LocalMemberState>(() => {
    const fatalTransportError = fatalTransportError$.value;
    if (fatalTransportError !== null) return fatalTransportError;
    const mediaState = mediaState$.value;
    const rtcSessionStatus = homeserverConnected.rtsSession$.value;
    // `mediaState` will be 'null' until the transport/connection appears.
    if (mediaState && rtcSessionStatus)
      return {
        matrix: fatalMatrixError$.value ?? rtcSessionStatus,
        media: publishError$.value ?? mediaState,
      };
    return TransportState.Waiting;
  });

  /**
   * The disconnect reason for the combined Matrix + LiveKit connection, or null
   * when fully connected. Homeserver reasons take priority over livekit.
   */
  const connectionDisconnectReason$ = computed(() => {
    const [hsConnected, hsReason] = homeserverConnected.combined$.value;
    if (!hsConnected) return hsReason!;
    if (localConnectionState$.value !== ConnectionState.LivekitConnected)
      return "livekit" as const;
    return null;
  });
  scope.effect(() => {
    logger.debug(
      "livekit+matrix: Connected state changed",
      connectionDisconnectReason$.value,
    );
  });

  /**
   * Whether we are "fully" connected to the call. Accounts for both the
   * connection to the MatrixRTC session and the LiveKit publish connection.
   */
  const matrixAndLivekitConnected$ = computed(
    () => connectionDisconnectReason$.value === null,
  );

  /**
   * Whether we should tell the user that we're reconnecting to the call: we
   * were connected, and now we're not.
   */
  const reconnecting$ = state(false);
  let wasConnected = matrixAndLivekitConnected$.value;
  scope.effect(() => {
    const connected = matrixAndLivekitConnected$.value;
    reconnecting$.value = wasConnected && !connected;
    wasConnected = connected;
  });

  // Report completed reconnection cycles to analytics. Only begin tracking
  // when transitioning FROM connected (null → non-null), so that the initial
  // startup phase — where we may be non-null before the first real
  // connection — isn't counted as a reconnect.
  let reconnectStart: {
    time: number;
    reason: NonNullable<(typeof connectionDisconnectReason$)["value"]>;
  } | null = null;
  let prevReason = connectionDisconnectReason$.value;
  scope.effect(() => {
    const reason = connectionDisconnectReason$.value;
    if (reason !== null) {
      if (prevReason === null) reconnectStart ??= { time: Date.now(), reason };
    } else if (reconnectStart !== null) {
      PosthogAnalytics.instance.eventCallReconnecting.track(
        roomId,
        reconnectStart.reason,
        (Date.now() - reconnectStart.time) / 1000,
      );
      PosthogAnalytics.instance.eventCallEnded.cacheReconnecting(
        reconnectStart.reason,
      );
      reconnectStart = null;
    }
    prevReason = reason;
  });

  // Inform the widget about the connect and disconnect intent from the user.
  let wasJoinRequested = joinAndPublishRequested$.value;
  scope.effect(() => {
    const joinRequested = joinAndPublishRequested$.value;
    const prev = wasJoinRequested;
    wasJoinRequested = joinRequested;
    if (!widget || prev === joinRequested) return;
    widget.api.transport
      .send(
        joinRequested
          ? ElementWidgetActions.JoinCall
          : ElementWidgetActions.HangupCall,
        {},
      )
      .catch((e) => {
        logger.error(
          `Failed to send ${joinRequested ? "join" : "hangup"} action`,
          e,
        );
      });
  });

  scope.effect(() => {
    const videoEnabled = muteStates.video.enabled$.value;
    void matrixRTCSession
      .updateCallIntent(videoEnabled ? "video" : "audio")
      .catch((e) => {
        if (e instanceof Error && e.message === "Not connected yet") {
          logger.debug(
            "'not connected yet' while updating the call intent (this is expected on startup)",
          );
        } else {
          throw e;
        }
      });
  });

  // Keep matrix rtc session in sync with advertisedTransport$, joinAndPublishRequested$
  scope.reconcile(
    computed(
      () =>
        [advertisedTransport$.value, joinAndPublishRequested$.value] as const,
    ),
    async ([transport, shouldConnect]) => {
      if (!transport) return;
      // if shouldConnect=false we will do the disconnect as the cleanup from the previous reconcile iteration.
      if (!shouldConnect) return;

      try {
        joinMatrixRTC(transport);
      } catch (error) {
        logger.error("Error entering RTC session", error);
        if (error instanceof Error)
          setMatrixError(new MembershipManagerError(error));
      }

      return Promise.resolve(async (): Promise<void> => {
        try {
          // TODO Update matrixRTCSession to allow udpating the transport without leaving the session!
          await matrixRTCSession.leaveRoomSession(1000);
        } catch (e) {
          logger.error("Error leaving RTC session", e);
        }
      });
    },
  );

  const participant$ = computed(
    () => localConnection$.value?.livekitRoom?.localParticipant ?? null,
  );
  scope.effect(() => {
    logger.debug("participant$ updated:", participant$.value?.identity);
  });

  // Pause upstream of all local media tracks when we're disconnected from
  // MatrixRTC, because it can be an unpleasant surprise for the app to say
  // 'reconnecting' and yet still be transmitting your media to others.
  // We use the homeserver connection rather than reconnecting$ because we want
  // to pause tracks during the initial joining sequence too until we're sure
  // that our own media is displayed on screen.
  // TODO refactor this based no livekitState$
  scope.effect(() => {
    const participant = participant$.value;
    const [connected] = homeserverConnected.combined$.value;
    if (!participant) return;
    for (const p of participant.trackPublications.values()) {
      const track = p.track;
      if (!track) continue;
      if (connected && track.isUpstreamPaused === true) {
        logger.info(
          `Resuming ${track.kind} track (MatrixRTC connection present)`,
        );
        track
          .resumeUpstream()
          .catch((e) =>
            logger.error(
              `Failed to resume ${track.kind} track after MatrixRTC reconnection`,
              e,
            ),
          );
      } else if (!connected && track.isUpstreamPaused === false) {
        logger.info(
          `Pausing ${track.kind} track (uncertain MatrixRTC connection)`,
        );
        track
          .pauseUpstream()
          .catch((e) =>
            logger.error(
              `Failed to pause ${track.kind} track after entering uncertain MatrixRTC connection`,
              e,
            ),
          );
      }
    }
  });

  /**
   * Whether the user is currently sharing their screen.
   */
  const sharingScreen$ = fromEvents(
    scope,
    participant$,
    PUBLICATION_EVENTS,
    (p) => p?.isScreenShareEnabled ?? false,
  );

  let toggleScreenSharing: (() => void) | null = null;
  if (
    "getDisplayMedia" in (navigator.mediaDevices ?? {}) &&
    !getUrlParams().hideScreensharing
  ) {
    toggleScreenSharing = (): void => {
      const screenshareSettings: ScreenShareCaptureOptions = {
        // Screen share audio shouldn't have any filtering.
        // "echoCancellation" is purposely excluded, as setting it to
        // false causes the screen share audio track to include
        // an echo of the incoming participant's voice
        audio: {
          autoGainControl: false,
          noiseSuppression: false,
          voiceIsolation: false,
        },
        selfBrowserSurface: "include",
        surfaceSwitching: "include",
        systemAudio: "include",
      };

      let publishOptions: TrackPublishOptions | undefined;

      if (advancedScreenShare.getValue()) {
        // User has advanced screen share settings enabled
        const { width, height } = parseResolution(
          screenShareResolution.getValue(),
        );
        const fps = screenShareFramerate.getValue();
        const bps = screenShareBitrate.getValue();
        const codec = screenShareCodec.getValue();

        screenshareSettings.resolution = {
          width,
          height,
          frameRate: fps,
        };

        publishOptions = {
          screenShareEncoding: {
            maxBitrate: bps,
            maxFramerate: fps,
          },
          videoCodec: codec,
        };
      } else {
        // Fall back to config.json settings if available
        const screenConf = Config.get().media_quality?.screen_share;
        if (screenConf?.max_resolution) {
          screenshareSettings.resolution = {
            width: Math.round((screenConf.max_resolution * 16) / 9),
            height: screenConf.max_resolution,
            frameRate: screenConf.max_framerate ?? 30,
          };
        }
      }

      const targetScreenshareState = !sharingScreen$.value;
      logger.info(
        `toggleScreenSharing called. Switching ${
          targetScreenshareState ? "On" : "Off"
        }`,
      );
      // If a connection is ready, toggle screen sharing.
      // We deliberately do nothing in the case of a null connection because
      // it looks nice for the call control buttons to all become available
      // at once upon joining the call, rather than introducing a disabled
      // state. The user can just click again.
      // We also allow screen sharing to be toggled even if the connection
      // is still initializing or publishing tracks, because there's no
      // technical reason to disallow this. LiveKit will publish if it can.
      participant$.value
        ?.setScreenShareEnabled(
          targetScreenshareState,
          screenshareSettings,
          publishOptions,
        )
        .catch(logger.error);
    };
  }

  return {
    startTracks,
    requestJoinAndPublish,
    requestDisconnect,
    localMemberState$,
    participant$,
    reconnecting$,
    connected$: matrixAndLivekitConnected$,
    disconnected$: computed(
      () =>
        homeserverConnected.rtsSession$.value === RTCSessionStatus.Disconnected,
    ),
    sharingScreen$,
    toggleScreenSharing,
    connection$: localConnection$,
    internalLoggerRef: logger,
  };
};

interface EnterRTCSessionOptions {
  encryptMedia: boolean;
  matrixRTCMode: MatrixRTCMode;
}

/**
 * Does the necessary steps to enter the RTC session on the matrix side:
 *  - Preparing the membership info (FOCUS to use, options)
 *  - Sends the matrix event to join the call, and starts the membership manager:
 *      - Delay events management
 *      - Handles retries (fails only after several attempts)
 *
 * @param rtcSession - The MatrixRTCSession to join.
 * @param ownMembershipIdentity - Options for entering the RTC session.
 * @param transport - The LivekitTransport to use for this session.
 * @param options - `encryptMedia`: Whether to encrypt media `matrixRTCMode`: The Matrix RTC mode to use.
 * @throws If the widget could not send ElementWidgetActions.JoinCall action.
 */
// Exported for unit testing
export function enterRTCSession(
  rtcSession: MatrixRTCSession,
  ownMembershipIdentity: CallMembershipIdentityParts,
  transport: LivekitTransportConfig,
  options: EnterRTCSessionOptions,
): void {
  const { encryptMedia, matrixRTCMode } = options;
  PosthogAnalytics.instance.eventCallEnded.cacheStartCall(new Date());
  PosthogAnalytics.instance.eventCallStarted.track(rtcSession.room.roomId);

  // This must be called before we start trying to join the call, as we need to
  // have started tracking by the time calls start getting created.
  // groupCallOTelMembership?.onJoinCall();

  const { matrix_rtc_session: matrixRtcSessionConfig } = Config.get();
  const { sendNotificationType: notificationType, callIntent } = getUrlParams();
  const multiSFU =
    matrixRTCMode === MatrixRTCMode.Compatibility ||
    matrixRTCMode === MatrixRTCMode.Matrix_2_0;

  // For backwards compatibility with Element Call versions that do not do Matrix 2.0,
  // we add the livekit alias to the transport.
  let backwardCompatibleTransport: LivekitTransport | LivekitTransportConfig;
  if (matrixRTCMode === MatrixRTCMode.Matrix_2_0) {
    backwardCompatibleTransport = transport;
  } else {
    backwardCompatibleTransport = {
      livekit_alias: rtcSession.room.roomId,
      ...transport,
    };
  }

  // Calculates `maximumNetworkErrorRetryCount`. The connection is failed if EITHER:
  // - The /sync loop is unresponsive for > `gracePeriod` ms, or
  // - A delayed leave event is emitted (after `leaveDelay` ms period).
  // Note: Use leaveDelay >> gracePeriod for delegated leave events.
  const gracePeriod = Config.get().sync_disconnect_grace_period_ms;
  const leaveDelay = matrixRtcSessionConfig?.delayed_leave_event_delay_ms;
  const retryInterval = matrixRtcSessionConfig?.network_error_retry_ms;

  // Math.min is used to account for the respective worst case: /sync not available or leave event emitted.
  const maxWaitTime = Math.min(gracePeriod, leaveDelay);
  const maximumNetworkErrorRetryCount =
    Math.ceil(maxWaitTime / retryInterval) + 1;

  // Multi-sfu does not need a preferred foci list. just the focus that is actually used.
  // TODO where/how do we track errors originating from the ongoing rtcSession?

  rtcSession.joinRTCSession(
    ownMembershipIdentity,
    multiSFU ? [] : [backwardCompatibleTransport],
    multiSFU ? backwardCompatibleTransport : undefined,
    {
      notificationType,
      callIntent,
      // The lobby said that joining a recorded call is consenting to it
      applicationData: consentOnJoin(
        rtcSession.room,
        computeSlotId(rtcSession.slotDescription),
        !getUrlParams().skipLobby,
      ),
      manageMediaKeys: encryptMedia,
      delayedLeaveEventRestartMs:
        matrixRtcSessionConfig?.delayed_leave_event_restart_ms,
      delayedLeaveEventDelayMs:
        matrixRtcSessionConfig?.delayed_leave_event_delay_ms,
      delayedLeaveEventRestartLocalTimeoutMs:
        matrixRtcSessionConfig?.delayed_leave_event_restart_local_timeout_ms,
      networkErrorRetryMs: matrixRtcSessionConfig?.network_error_retry_ms,
      makeKeyDelay: matrixRtcSessionConfig?.wait_for_key_rotation_ms,
      membershipEventExpiryMs:
        matrixRtcSessionConfig?.membership_event_expiry_ms,
      keyRotationParticipantLimit:
        matrixRtcSessionConfig?.key_rotation_participant_limit,
      unstableSendStickyEvents: matrixRTCMode === MatrixRTCMode.Matrix_2_0,
      maximumNetworkErrorRetryCount: maximumNetworkErrorRetryCount,
    },
  );
}
